/**
 * Left Slide Panel Manager - Slides out from left edge
 * Handles the collapsible left slide-out panel
 */
export class LeftPanelManager {
  constructor() {
    this.panel = null;
    this.header = null;
    this.content = null;
    this.toggleBtn = null;
    this.closeBtn = null;
    this.isOpen = false;
    this.sections = new Map(); // sectionId -> section data
    this.activeSection = null;

    this.init();
  }

  /**
   * Initialize the left slide panel
   */
  init() {
    console.log('🔧 LeftPanelManager: Initializing slide panel...');
    this.panel = document.getElementById('left-slide-panel');
    this.header = document.getElementById('left-slide-header');
    this.content = document.getElementById('left-slide-content');
    this.toggleBtn = document.getElementById('left-panel-toggle');
    this.closeBtn = document.getElementById('slide-close-btn');

    console.log('🔍 LeftPanelManager: Elements found:', {
      panel: !!this.panel,
      header: !!this.header,
      content: !!this.content,
      toggleBtn: !!this.toggleBtn,
      closeBtn: !!this.closeBtn
    });

    if (!this.panel || !this.toggleBtn) {
      console.error('❌ LeftPanelManager: Required elements not found!');
      return;
    }

    console.log('✅ LeftPanelManager: Elements initialized successfully');
    this.setupEventListeners();
    this.createDefaultSections();
  }

  /**
   * Set up event listeners for the panel
   */
  setupEventListeners() {
    // Toggle button to open panel
    this.toggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openPanel();
    });

    // Close button to close panel
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closePanel();
      });
    }

    // Click outside to close panel
    document.addEventListener('click', (e) => {
      if (false && this.isOpen && !this.panel.contains(e.target) && e.target !== this.toggleBtn) {
        this.closePanel();
      }
    });

    // Prevent panel clicks from closing it
    this.panel.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // ESC key to close panel
    document.addEventListener('keydown', (e) => {
      if (false && e.key === 'Escape' && this.isOpen) {
        this.closePanel();
      }
    });
  }

  /**
   * Open the slide panel
   */
  openPanel() {
    this.isOpen = true;
    this.panel.classList.add('open');
    this.emitEvent('panelOpened');
    console.log('📱 Left slide panel opened');
  }

  /**
   * Close the slide panel
   */
  closePanel() {
    this.isOpen = false;
    this.panel.classList.remove('open');
    this.emitEvent('panelClosed');
    console.log('📱 Left slide panel closed');
  }

  /**
   * Toggle panel open/close
   */
  togglePanel() {
    if (this.isOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  /**
   * Render Live/Replay mode toggle at the top of the panel (outside sections)
   */
  renderModeToggle() {
    if (!this.content) return;

    const toggleContainer = document.createElement('div');
    toggleContainer.className = 'mode-toggle-container';
    toggleContainer.innerHTML = `
      <div class="replay-mode-toggle" id="replay-mode-toggle">
        <button class="replay-mode-btn active" id="mode-live-btn" data-mode="live">
          <svg class="mode-icon live-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"/>
          </svg>
          <span class="mode-label">Live</span>
          <span class="live-indicator"></span>
        </button>
        <button class="replay-mode-btn" id="mode-replay-btn" data-mode="replay">
          <svg class="mode-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M8 16H3v5"/>
          </svg>
          <span class="mode-label">Replay</span>
        </button>
        <button class="replay-mode-btn" id="mode-igc-btn" data-mode="igc">
          <svg class="mode-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <span class="mode-label">IGC</span>
        </button>
      </div>
    `;

    // Insert at the very top of the content
    this.content.insertBefore(toggleContainer, this.content.firstChild);
  }

  /**
   * Create default navigation sections with professional SVG icons
   */
  createDefaultSections() {
    // Add mode toggle at the very top (outside sections)
    this.renderModeToggle();

    // Aircraft section (renamed from Labels & Trails) with plane icon
    this.addSection('aircraft', '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg> Aircraft Display', [
      { id: 'trail-length', label: 'Trail Length', type: 'dropdown', options: ['25', '50', '100', '200', '300', '500', '1000'], defaultValue: '300' },
      {
        id: 'info-label', label: 'Info Label', type: 'dropdown', options: [
          { value: 'id', label: 'Off' },
          { value: 'altitude', label: 'Altitude' },
          { value: 'speed', label: 'Speed' },
          { value: 'ddh', label: 'Finish Alt Δ (DDH)' },
          { value: 'etf_delta', label: 'Recent XC Pace A' },
          { value: 'etf_delta_b', label: 'Recent XC Pace B' },
          { value: 'vario_kalman', label: 'Vario (30s)k' }
        ], defaultValue: 'etf_delta'
      },
      { id: 'link-toggle', type: 'link-toggle' },
      {
        id: 'colors', label: 'Colors', type: 'dropdown', options: [
          { value: 'aircraft', label: 'Aircraft' },
          { value: 'altitude', label: 'Altitude' },
          { value: 'speed', label: 'Speed' },
          { value: 'etf_delta', label: 'Recent XC Pace A' },
          { value: 'etf_delta_b', label: 'Recent XC Pace B' },
          { value: 'vario_kalman', label: 'Vario (30s)k' }
        ], defaultValue: 'etf_delta'
      },
      {
        id: 'trend-color-scheme', label: 'Color Scheme', type: 'dropdown', options: [
          { value: 'gain_loss', label: 'Red-Green' },
          { value: 'legacy_blue_red', label: 'Blue-Red' }
        ], defaultValue: 'gain_loss'
      },
      { id: 'color-sensitivity', label: 'Color Sensitivity', type: 'range', min: -100, max: 100, step: 5, defaultValue: '0' },
      { id: 'info-help', type: 'help-inline' },
      { id: 'trend-window', label: 'Recent XC Pace window A', type: 'number', min: 30, max: 600, step: 30, defaultValue: '60', unit: 's' },
      { id: 'trend-window-b', label: 'Recent XC Pace window B', type: 'number', min: 30, max: 3600, step: 30, defaultValue: '300', unit: 's' },
      {
        id: 'units', label: 'Units', type: 'dropdown', options: [
          { value: 'imperial', label: 'Imperial' },
          { value: 'metric', label: 'Metric' }
        ], defaultValue: 'metric'
      },
      {
        id: 'cn-label-size', label: 'CN Label Size', type: 'dropdown', options: [
          { value: '12', label: 'Tiny' },
          { value: '14', label: 'Small' },
          { value: '16', label: 'Medium' },
          { value: '18', label: 'Large' },
          { value: '24', label: 'X-Large' }
        ], defaultValue: '16'
      },
      { id: 'replay-refresh-rate', label: 'Replay Refresh', type: 'range', min: 1, max: 200, step: 1, defaultValue: '10', tooltip: 'Limits how often stats, labels, and leaderboard update during fast replay (5x–20x). Lower = smoother, higher = more responsive.' },
      { id: 'leaderboard-settings-link', label: 'Leaderboard settings →', type: 'mini-link' },
      { id: 'leaderboard-settings', type: 'leaderboard-settings' }
    ], false);

    // Servers section
    this.addSection('servers', 'Servers Online', [
      { id: 'server-selector', type: 'server-list' }
    ]);

    // Tasks section with filter buttons
    this.addSection('tasks', 'Tasks', [
      { id: 'task-filters', type: 'task-filters' },
      { id: 'task-selector', type: 'task-list' }
    ]);

    // Statistics section with icon (moved to bottom)
    this.addSection('statistics', '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> Live Statistics', [
      { id: 'aircraft', label: 'Aircraft Flying', value: '0', type: 'text-stat' },
      { id: 'servers', label: 'Servers Online', value: '0', type: 'text-stat' },
      { id: 'viewers', label: 'People Watching', value: '-', type: 'text-stat' }
    ], false);

    // Help section (always at bottom)
    this.addSection('help', '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> Help', [
      { id: 'get-app', label: 'Get the App', type: 'help-link', url: '/gettheapp' }
    ], false);
  }

  /**
   * Add a new section to the panel
   * @param {string} sectionId - Unique identifier for the section
   * @param {string} title - Display title for the section
   * @param {Array} items - Array of navigation items
   * @param {boolean} startExpanded - Whether section starts expanded (default: true)
   */
  addSection(sectionId, title, items = [], startExpanded = true) {
    const sectionData = {
      id: sectionId,
      title,
      items,
      startExpanded,
      element: null,
      headerElement: null,
      contentElement: null
    };

    this.sections.set(sectionId, sectionData);
    this.renderSection(sectionData);

    return sectionData;
  }

  /**
   * Remove a section from the panel
   * @param {string} sectionId - ID of the section to remove
   */
  removeSection(sectionId) {
    const section = this.sections.get(sectionId);
    if (section && section.element) {
      section.element.remove();
    }
    this.sections.delete(sectionId);
  }

  /**
   * Render a section in the panel
   * @param {Object} sectionData - Section data object
   */
  renderSection(sectionData) {
    const { id, title, items, startExpanded } = sectionData;

    // Create section container
    const sectionElement = document.createElement('div');
    sectionElement.className = startExpanded ? 'left-section expanded' : 'left-section';
    sectionElement.dataset.sectionId = id;

    // Create section header with chevron
    const headerElement = document.createElement('div');
    headerElement.className = 'left-section-header';

    // Add chevron icon
    const chevronIcon = document.createElement('span');
    chevronIcon.className = 'section-chevron';
    chevronIcon.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>';

    // Add title content
    const titleContent = document.createElement('span');
    titleContent.className = 'section-title-content';
    titleContent.innerHTML = title; // Use innerHTML to support SVG icons

    headerElement.appendChild(chevronIcon);
    headerElement.appendChild(titleContent);
    headerElement.addEventListener('click', () => this.toggleSection(id));

    // Create section content
    const contentElement = document.createElement('div');
    contentElement.className = 'left-section-content';

    // Add navigation items
    items.forEach(item => {
      const itemElement = this.createNavItem(item);
      if (itemElement) {
        contentElement.appendChild(itemElement);
      }
    });

    // Assemble section
    sectionElement.appendChild(headerElement);
    sectionElement.appendChild(contentElement);

    // Store references
    sectionData.element = sectionElement;
    sectionData.headerElement = headerElement;
    sectionData.contentElement = contentElement;
    sectionData.isExpanded = startExpanded; // Track expansion state

    // Add to panel
    this.content.appendChild(sectionElement);
  }

  /**
   * Create a navigation item element
   * @param {Object} item - Item configuration
   */
  createNavItem(item) {
    const { id, label, iconSvg, type, value, options, defaultValue } = item;

    // Handle different item types
    if (type === 'text-stat') {
      // Plain text statistics display (non-clickable)
      const statElement = document.createElement('div');
      statElement.className = 'text-stat-item';
      statElement.dataset.itemId = id;
      statElement.innerHTML = `
        <span class="text-stat-label">${label}</span>
        <span class="text-stat-value" id="stat-${id}">${value || '0'}</span>
      `;
      return statElement;
    }

    if (type === 'dropdown') {
      // Dropdown selector item with label on left
      const dropdownElement = document.createElement('div');
      dropdownElement.className = 'dropdown-item';

      // Handle both simple array and object array formats
      const optionsHTML = options.map(opt => {
        if (typeof opt === 'object') {
          return `<option value="${opt.value}" ${opt.value === defaultValue ? 'selected' : ''}>${opt.label}</option>`;
        } else {
          return `<option value="${opt}" ${opt === defaultValue ? 'selected' : ''}>${opt}</option>`;
        }
      }).join('');

      // Add optional note (e.g., "3D Only")
      const noteHTML = item.note ? `<span class="dropdown-note">${item.note}</span>` : '';

      dropdownElement.innerHTML = `
        <label class="dropdown-label">${label}${noteHTML}</label>
        <select class="dropdown-select" id="dropdown-${id}">
          ${optionsHTML}
        </select>
      `;
      return dropdownElement;
    }

    if (type === 'number') {
      const numberElement = document.createElement('div');
      numberElement.className = item.unit ? 'dropdown-item unit-suffix-item' : 'dropdown-item';
      const minAttr = item.min != null ? `min="${item.min}"` : '';
      const maxAttr = item.max != null ? `max="${item.max}"` : '';
      const stepAttr = item.step != null ? `step="${item.step}"` : '';
      const unitHTML = item.unit ? `<span class="dropdown-unit">${item.unit}</span>` : '';
      if (item.unit) {
        numberElement.innerHTML = `
          <label class="dropdown-label">${label}</label>
          <div class="input-with-unit">
            <input class="dropdown-select" id="dropdown-${id}" type="number"
                   value="${defaultValue || ''}" ${minAttr} ${maxAttr} ${stepAttr}>
            ${unitHTML}
          </div>
        `;
      } else {
        numberElement.innerHTML = `
          <label class="dropdown-label">${label}</label>
          <input class="dropdown-select" id="dropdown-${id}" type="number"
                 value="${defaultValue || ''}" ${minAttr} ${maxAttr} ${stepAttr}>
        `;
      }
      return numberElement;
    }

    if (type === 'range') {
      const rangeElement = document.createElement('div');
      rangeElement.className = 'dropdown-item range-item';
      rangeElement.id = `range-row-${id}`;
      if (item.tooltip) rangeElement.title = item.tooltip;
      const minAttr = item.min != null ? `min="${item.min}"` : '';
      const maxAttr = item.max != null ? `max="${item.max}"` : '';
      const stepAttr = item.step != null ? `step="${item.step}"` : '';
      const sliderClass = id === 'color-sensitivity' ? 'color-sensitivity-slider' : 'range-slider-neutral';
      rangeElement.innerHTML = `
        <label class="dropdown-label">${label}</label>
        <input class="${sliderClass}" id="dropdown-${id}" type="range"
               value="${defaultValue || 0}" ${minAttr} ${maxAttr} ${stepAttr}>
        <span class="range-value-label" id="dropdown-${id}-label"></span>
      `;
      return rangeElement;
    }

    if (type === 'link-toggle') {
      // Link toggle button between Info Label and Colors
      const linkElement = document.createElement('div');
      linkElement.className = 'link-toggle-container';
      linkElement.innerHTML = `
        <button class="link-toggle-btn active" id="link-toggle-btn" title="Link Info Label and Colors">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
          </svg>
        </button>
      `;
      return linkElement;
    }

    if (type === 'server-list') {
      // Server list container - will be populated by main.js updateServersPanel()
      const serverElement = document.createElement('div');
      serverElement.className = 'server-list-container';
      serverElement.id = 'left-servers-content'; // New ID for left panel
      serverElement.innerHTML = '<div class="empty-state">No servers available</div>';
      return serverElement;
    }

    if (type === 'help-inline') {
      const helpEl = document.createElement('div');
      helpEl.className = 'dropdown-help';
      helpEl.id = 'left-etf-help';
      helpEl.style.display = 'none';
      return helpEl;
    }

    if (type === 'mini-link') {
      const linkEl = document.createElement('div');
      linkEl.className = 'mini-link-row';
      linkEl.innerHTML = `
        <a href="#" id="show-leaderboard-settings-btn" class="mini-link-btn">${label}</a>
        <a href="leaderboard.html" target="_blank" class="mini-link-btn" title="Open the streaming overlay leaderboard in a new window">View streaming overlay →</a>
      `;
      return linkEl;
    }

    if (type === 'leaderboard-settings') {
      const settingsEl = document.createElement('div');
      settingsEl.id = 'leaderboard-settings-container';
      settingsEl.className = 'leaderboard-settings-panel';
      settingsEl.style.display = 'none';
      settingsEl.innerHTML = `
        <div class="dropdown-item unit-suffix-item">
          <label class="dropdown-label">XC Pace A update (game time)</label>
          <div class="input-with-unit">
            <input class="dropdown-select" id="dropdown-overlay-xc-a-update-sec" type="number" value="1" min="1" max="600" step="1">
            <span class="dropdown-unit">s</span>
          </div>
        </div>

        <div class="dropdown-item unit-suffix-item">
          <label class="dropdown-label">XC Pace B update (game time)</label>
          <div class="input-with-unit">
            <input class="dropdown-select" id="dropdown-overlay-xc-b-update-sec" type="number" value="1" min="1" max="600" step="1">
            <span class="dropdown-unit">s</span>
          </div>
        </div>

        <div class="dropdown-item unit-suffix-item">
          <label class="dropdown-label">Rank change window (game time)</label>
          <div class="input-with-unit">
            <input class="dropdown-select" id="dropdown-overlay-position-window-sec" type="number" value="300" min="30" max="3600" step="30">
            <span class="dropdown-unit">s</span>
          </div>
        </div>

        <div class="dropdown-item unit-suffix-item">
          <label class="dropdown-label">Rank change refresh (game time)</label>
          <div class="input-with-unit">
            <input class="dropdown-select" id="dropdown-overlay-position-refresh-sec" type="number" value="10" min="1" max="600" step="1">
            <span class="dropdown-unit">s</span>
          </div>
        </div>

        <div class="dropdown-item unit-suffix-item">
          <label class="dropdown-label">Overlay rotation</label>
          <div class="input-with-unit">
            <input class="dropdown-select" id="dropdown-overlay-rotation-sec" type="number" value="10" min="1" max="120" step="1">
            <span class="dropdown-unit">s</span>
          </div>
        </div>

        <div class="overlay-columns-panel">
          <div class="overlay-columns-title">Overlay columns</div>

          <div class="overlay-columns-row">
            <label class="overlay-columns-label">Available stats</label>
            <select class="overlay-column-select" id="overlay-available-columns"></select>
            <div class="overlay-columns-buttons compact">
              <button type="button" class="overlay-columns-btn" id="overlay-add-permanent-btn">Add permanent</button>
              <button type="button" class="overlay-columns-btn" id="overlay-add-rotating-btn">Add rotating</button>
            </div>
          </div>

          <div class="overlay-columns-row">
            <label class="overlay-columns-label">Permanent cols (ordered)</label>
            <select class="overlay-column-list" id="overlay-permanent-columns" multiple size="6"></select>
            <div class="overlay-columns-buttons">
              <button type="button" class="overlay-columns-btn" id="overlay-permanent-up-btn">Up</button>
              <button type="button" class="overlay-columns-btn" id="overlay-permanent-down-btn">Down</button>
              <button type="button" class="overlay-columns-btn danger" id="overlay-remove-permanent-btn">Remove</button>
            </div>
          </div>

          <div class="overlay-columns-row">
            <label class="overlay-columns-label">Rotating last-column set</label>
            <select class="overlay-column-list" id="overlay-rotating-columns" multiple size="6"></select>
            <div class="overlay-columns-buttons">
              <button type="button" class="overlay-columns-btn" id="overlay-rotating-up-btn">Up</button>
              <button type="button" class="overlay-columns-btn" id="overlay-rotating-down-btn">Down</button>
              <button type="button" class="overlay-columns-btn danger" id="overlay-remove-rotating-btn">Remove</button>
            </div>
          </div>
        </div>
      `;
      return settingsEl;
    }

    if (type === 'task-filters') {
      // Task filter buttons (Live Now / By Server)
      const filterElement = document.createElement('div');
      filterElement.className = 'task-filters';
      filterElement.innerHTML = `
        <button class="task-filter-btn active" id="left-filter-live-now" data-filter="liveNow">Live Now</button>
        <button class="task-filter-btn" id="left-filter-by-server" data-filter="byServer">By Server</button>
      `;
      return filterElement;
    }

    if (type === 'task-list') {
      // Task list container - will be populated by main.js
      const taskElement = document.createElement('div');
      taskElement.className = 'task-list-container';
      taskElement.id = 'left-task-list'; // New ID for left panel
      taskElement.innerHTML = '<div class="empty-state">No tasks found</div>';
      return taskElement;
    }

    if (type === 'replay-mode-toggle') {
      // Replay mode toggle buttons (Live / Replay)
      const toggleElement = document.createElement('div');
      toggleElement.className = 'replay-mode-toggle';
      toggleElement.id = 'replay-mode-toggle';
      toggleElement.innerHTML = `
        <button class="replay-mode-btn active" id="mode-live-btn" data-mode="live">
          <svg class="mode-icon live-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"/>
          </svg>
          <span class="mode-label">Live</span>
          <span class="live-indicator"></span>
        </button>
        <button class="replay-mode-btn" id="mode-replay-btn" data-mode="replay">
          <svg class="mode-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M8 16H3v5"/>
          </svg>
          <span class="mode-label">Replay</span>
        </button>
        <button class="replay-mode-btn" id="mode-igc-btn-bottom" data-mode="igc">
          <svg class="mode-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <span class="mode-label">IGC</span>
        </button>
      `;
      return toggleElement;
    }

    if (type === 'coming-soon') {
      // Coming soon message
      const comingSoonElement = document.createElement('div');
      comingSoonElement.className = 'coming-soon-item';
      comingSoonElement.dataset.itemId = id;
      comingSoonElement.innerHTML = `
        <span class="coming-soon-text">Coming Soon</span>
      `;
      return comingSoonElement;
    }

    if (type === 'help-link') {
      // Help link item
      const linkElement = document.createElement('a');
      linkElement.href = item.url || '#';
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.className = 'left-nav-item help-link-item';
      linkElement.dataset.itemId = id;
      linkElement.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
          <polyline points="15 3 21 3 21 9"/>
          <line x1="10" y1="14" x2="21" y2="3"/>
        </svg>
        <span>${label}</span>
      `;
      return linkElement;
    }

    // Default navigation item (for waypoint, route, etc.)
    const itemElement = document.createElement('div');
    itemElement.className = 'left-nav-item';
    itemElement.dataset.itemId = id;

    const labelElement = document.createElement('span');
    labelElement.textContent = label;

    itemElement.appendChild(labelElement);

    // Add click handler
    itemElement.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleNavItemClick(id, item);
    });

    return itemElement;
  }

  /**
   * Toggle section expansion
   * @param {string} sectionId - ID of section to toggle
   */
  toggleSection(sectionId) {
    const section = this.sections.get(sectionId);
    if (!section) return;

    // Toggle expanded state
    section.isExpanded = !section.isExpanded;

    if (section.isExpanded) {
      section.element.classList.add('expanded');
    } else {
      section.element.classList.remove('expanded');
    }

    // Emit event for external handlers
    this.emitEvent('sectionToggled', { sectionId, section, isExpanded: section.isExpanded });
  }

  /**
   * Handle navigation item click
   * @param {string} itemId - ID of clicked item
   * @param {Object} item - Item configuration object
   */
  handleNavItemClick(itemId, item) {
    // Remove active class from all items
    document.querySelectorAll('.left-nav-item').forEach(el => {
      el.classList.remove('active');
    });

    // Add active class to clicked item
    const itemElement = document.querySelector(`[data-item-id="${itemId}"]`);
    if (itemElement) {
      itemElement.classList.add('active');
    }

    // Emit event for external handlers
    this.emitEvent('navItemClicked', { itemId, item });
  }

  /**
   * Set active navigation item
   * @param {string} itemId - ID of item to activate
   */
  setActiveItem(itemId) {
    this.handleNavItemClick(itemId, { id: itemId });
  }

  /**
   * Get all available sections
   * @returns {Map} Map of section data
   */
  getSections() {
    return this.sections;
  }

  /**
   * Get section by ID
   * @param {string} sectionId - Section ID
   * @returns {Object|null} Section data or null
   */
  getSection(sectionId) {
    return this.sections.get(sectionId) || null;
  }

  /**
   * Update section content
   * @param {string} sectionId - Section ID
   * @param {Array} newItems - New items array
   */
  updateSection(sectionId, newItems) {
    const section = this.sections.get(sectionId);
    if (!section) return;

    section.items = newItems;
    section.contentElement.innerHTML = '';

    newItems.forEach(item => {
      const itemElement = this.createNavItem(item);
      section.contentElement.appendChild(itemElement);
    });
  }

  /**
   * Event emission system
   */
  emitEvent(eventName, data = {}) {
    const event = new CustomEvent(`leftPanel:${eventName}`, {
      detail: { ...data, panel: this }
    });
    document.dispatchEvent(event);
  }

  /**
   * Add event listener for panel events
   * @param {string} eventName - Event name
   * @param {Function} callback - Callback function
   */
  addEventListener(eventName, callback) {
    document.addEventListener(`leftPanel:${eventName}`, callback);
  }

  /**
   * Remove event listener
   * @param {string} eventName - Event name
   * @param {Function} callback - Callback function
   */
  removeEventListener(eventName, callback) {
    document.removeEventListener(`leftPanel:${eventName}`, callback);
  }
}
